import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { markdownToHtml } from '../ai/utils/markdown';
import PageInfoButton from '@/components/PageInfoButton';

interface Page {
  id: number;
  slug: string;
  title: string;
  status: string;
  language: string;
  current_version_id: number | null;
  published_at: string | null;
  updated_at: string;
}

interface PageVersion {
  id: number;
  page_id: number;
  version_number: number;
  title: string;
  content: string;
  meta_description: string | null;
  meta_keywords: string | null;
  change_note: string | null;
  status: string;
  author_name?: string;
  author_last?: string;
  created_at: string;
}

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

export default function PagesAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);
  const [versions, setVersions] = useState<PageVersion[]>([]);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editor state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [aiConverting, setAiConverting] = useState(false);
  const [aiAction, setAiAction] = useState<string | null>(null);

  const handleAiConvert = async (action: string) => {
    if (!content.trim() || aiConverting) return;
    setAiConverting(true);
    setAiAction(action);
    try {
      const res = await fetch('/api/pages/admin/ai-convert', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ text: content, action }),
      });
      const data = await res.json();
      if (data.success && data.markdown) {
        setContent(data.markdown);
      } else {
        alert(data.message || 'AI conversion failed');
      }
    } catch {
      alert('AI conversion failed — check your connection');
    } finally {
      setAiConverting(false);
      setAiAction(null);
    }
  };


  const fetchPages = useCallback(async () => {
    try {
      const res = await fetch('/api/pages/admin/list', { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setPages(data.pages || []);
    } catch {}
  }, []);

  // Auto-open page from ?edit=ID query param
  const editIdFromUrl = searchParams.get('edit');

  useEffect(() => { fetchPages(); }, [fetchPages]);

  useEffect(() => {
    if (editIdFromUrl && pages.length > 0 && !selectedPage) {
      const target = pages.find(p => String(p.id) === editIdFromUrl);
      if (target) {
        selectPage(target);
        setEditing(true);
        // Clear the query param
        setSearchParams({}, { replace: true });
      }
    }
  }, [editIdFromUrl, pages]);

  const selectPage = async (page: Page) => {
    setSelectedPage(page);
    setEditing(false);
    setCreating(false);
    try {
      const res = await fetch(`/api/pages/admin/${page.id}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setVersions(data.versions || []);
        // Load latest version into editor
        const latest = data.versions?.[0];
        if (latest) {
          setTitle(latest.title);
          setContent(latest.content);
          setMetaDesc(latest.meta_description || '');
        }
      }
    } catch {}
  };

  const startCreate = () => {
    setSelectedPage(null);
    setCreating(true);
    setEditing(false);
    setTitle('');
    setSlug('');
    setContent('');
    setMetaDesc('');
    setChangeNote('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (creating) {
        const res = await fetch('/api/pages/admin', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ slug, title, content, meta_description: metaDesc, change_note: changeNote || 'Initial version' }),
        });
        const data = await res.json();
        if (data.success) {
          await fetchPages();
          setCreating(false);
          if (data.page) selectPage(data.page);
        } else {
          alert(data.message || 'Failed to create page');
        }
      } else if (selectedPage) {
        const res = await fetch(`/api/pages/admin/${selectedPage.id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ title, content, meta_description: metaDesc, change_note: changeNote || `Updated ${new Date().toLocaleDateString()}` }),
        });
        const data = await res.json();
        if (data.success) {
          setEditing(false);
          setChangeNote('');
          selectPage(selectedPage);
          await fetchPages();
        }
      }
    } catch {
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (versionId: number) => {
    if (!selectedPage) return;
    try {
      await fetch(`/api/pages/admin/${selectedPage.id}/publish/${versionId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: '{}',
      });
      selectPage(selectedPage);
      await fetchPages();
    } catch {}
  };

  const handleDelete = async (pageId: number) => {
    if (!confirm('Are you sure you want to delete this page? This cannot be undone.')) return;
    try {
      await fetch(`/api/pages/admin/${pageId}`, { method: 'DELETE', headers: authHeaders() });
      setSelectedPage(null);
      setVersions([]);
      await fetchPages();
    } catch {}
  };

  const statusColor = (s: string) => {
    switch (s) {
      case 'published': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'draft': return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
      case 'pending_review': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'archived': return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 dark:text-white">Pages</h1><PageInfoButton /></div>
          <p className="text-sm text-slate-500 mt-1">Manage policy pages, terms, and other static content</p>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Page
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6" style={{ height: 'calc(100vh - 160px)' }}>
        {/* Page List — scrollable */}
        <div className="col-span-4 overflow-hidden flex flex-col" style={{ height: '100%' }}>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ height: '100%' }}>
            <div className="p-3 border-b border-slate-100 dark:border-slate-700 shrink-0">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{pages.length} Pages</p>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700 overflow-y-auto flex-1">
              {pages.map(page => (
                <div
                  key={page.id}
                  onClick={() => selectPage(page)}
                  className={`p-3 cursor-pointer transition-colors ${
                    selectedPage?.id === page.id ? 'bg-blue-50 dark:bg-blue-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">{page.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor(page.status)}`}>{page.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-mono">/{page.slug}</span>
                    <span className="text-[10px] text-slate-400">{page.language}</span>
                    {page.status === 'published' && (
                      <a
                        href={`/page/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-[10px] text-blue-500 hover:text-blue-400 ml-auto"
                      >
                        Preview
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {pages.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-400">No pages yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Editor / Detail — fixed, scrollable */}
        <div className="col-span-8 overflow-y-auto" style={{ height: '100%' }}>
          {!selectedPage && !creating ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12">
              <p className="text-sm text-slate-400">Select a page to edit or create a new one</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-slate-900 dark:text-white">
                    {creating ? 'Create New Page' : selectedPage?.title}
                  </h2>
                  {selectedPage && !creating && (
                    <p className="text-xs text-slate-400 mt-0.5">/{selectedPage.slug} | {versions.length} versions</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {!creating && !editing && (
                    <>
                      <a
                        href={`/page/${selectedPage?.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 inline-flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        Preview
                      </a>
                      <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit</button>
                      <button onClick={() => handleDelete(selectedPage!.id)} className="px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-600 rounded-lg hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400">Delete</button>
                    </>
                  )}
                  {(editing || creating) && (
                    <>
                      <button onClick={() => { setEditing(false); setCreating(false); }} className="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
                      <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Editor */}
              {(editing || creating) ? (
                <div className="p-4 space-y-4">
                  {creating && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Title</label>
                        <input value={title} onChange={e => { setTitle(e.target.value); if (creating) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')); }} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" placeholder="Privacy Policy" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Slug</label>
                        <input value={slug} onChange={e => setSlug(e.target.value)} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-mono" placeholder="privacy-policy" />
                      </div>
                    </div>
                  )}
                  {!creating && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Title</label>
                      <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-semibold text-slate-500">Content (Markdown)</label>
                      {/* AI Toolbar */}
                      <div className="flex items-center gap-1">
                        {[
                          { key: 'to-markdown', label: 'Convert to Markdown', icon: 'M' },
                          { key: 'improve', label: 'Improve', icon: 'I' },
                          { key: 'expand', label: 'Expand', icon: 'E' },
                          { key: 'summarise', label: 'Summarise', icon: 'S' },
                        ].map(btn => (
                          <button
                            key={btn.key}
                            onClick={() => handleAiConvert(btn.key)}
                            disabled={aiConverting || !content.trim()}
                            title={btn.label}
                            className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-all ${
                              aiConverting && aiAction === btn.key
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 animate-pulse'
                                : 'bg-slate-100 text-slate-500 hover:bg-purple-50 hover:text-purple-600 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-purple-900/20 dark:hover:text-purple-400'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            {aiConverting && aiAction === btn.key ? (
                              <span className="flex items-center gap-1">
                                <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                AI...
                              </span>
                            ) : (
                              <>
                                <svg className="w-3 h-3 inline mr-0.5 -mt-px" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a4 4 0 014 4v1a1 1 0 001 1h1a4 4 0 010 8h-1a1 1 0 00-1 1v1a4 4 0 01-8 0v-1a1 1 0 00-1-1H6a4 4 0 010-8h1a1 1 0 001-1V6a4 4 0 014-4z"/></svg>
                                {btn.label}
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={content}
                      onChange={e => setContent(e.target.value)}
                      className="w-full min-h-[400px] px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-y-auto"
                      placeholder="# Page Title

## Section

Content here...

Paste plain text and click 'Convert to Markdown' to auto-format with AI."
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Markdown editor with AI assist. Paste text and use the AI buttons above to convert, improve, expand, or summarise.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Meta Description</label>
                      <input value={metaDesc} onChange={e => setMetaDesc(e.target.value)} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" placeholder="SEO description" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Change Note</label>
                      <input value={changeNote} onChange={e => setChangeNote(e.target.value)} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" placeholder="What changed?" />
                    </div>
                  </div>
                </div>
              ) : (
                /* Preview + Versions */
                <div>
                  {/* Content Preview */}
                  {versions[0] && (
                    <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Current Content</h3>
                      <div className="prose prose-sm dark:prose-invert max-w-none max-h-64 overflow-y-auto" dangerouslySetInnerHTML={{ __html: markdownToHtml(versions[0].content) }} />
                    </div>
                  )}

                  {/* Version History */}
                  <div className="p-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Version History</h3>
                    <div className="space-y-2">
                      {versions.map(v => (
                        <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                          <div>
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">v{v.version_number}</span>
                            <span className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColor(v.status)}`}>{v.status}</span>
                            <span className="ml-2 text-xs text-slate-400">{v.change_note || ''}</span>
                            <span className="ml-2 text-[10px] text-slate-400">{new Date(v.created_at).toLocaleDateString()}</span>
                            {v.author_name && <span className="ml-2 text-[10px] text-slate-400">by {v.author_name}</span>}
                          </div>
                          <div className="flex gap-1">
                            {v.version_number !== versions[0]?.version_number && (
                              <button
                                onClick={() => {
                                  setTitle(v.title);
                                  setContent(v.content);
                                  setMetaDesc(v.meta_description || '');
                                  setChangeNote(`Reverted to v${v.version_number}`);
                                  setEditing(true);
                                }}
                                className="px-2 py-1 text-[10px] font-bold text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                              >
                                Revert
                              </button>
                            )}
                            {v.status !== 'published' && (
                              <button
                                onClick={() => handlePublish(v.id)}
                                className="px-2 py-1 text-[10px] font-bold text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              >
                                Publish
                              </button>
                            )}
                            {v.status === 'published' && (
                              <span className="text-[10px] font-bold text-green-500 px-2 py-1">Live</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
